/**
 * sprayWallMap — the spray wall's presence on the gym map, in one place.
 *
 * The map (MapTab / ZoomableMap), the app shell (useMainApp) and the wall
 * editor of the Spraywall tab all need to agree on the same few questions:
 * which walls get a marker, what hash opens a wall, how a wall gets placed or
 * unplaced. Keeping the rules here means the marker, the legend row and the
 * placement flow cannot drift apart — a wall the legend counts but the map
 * does not draw (or the reverse) is exactly the kind of silent mismatch this
 * file prevents.
 *
 * Everything is pure or takes its side-effecting collaborators as arguments,
 * so it is unit-testable without React.
 */
import { serverTimestamp, deleteField } from "firebase/firestore";
import { buildSprayWallPlacement } from "./sprayWallDoc";

/** Tab key of the Spraywall tab (built in step 4); also the hash root. */
export const SPRAY_WALL_TAB = "spraywall";

/**
 * Whether `wall` gets a marker on the map `mapId`.
 * Draft walls (no published version), hidden walls, walls whose cascade delete
 * has started and walls without finite coordinates are never drawn — the rule
 * is identical for every viewer, staff included.
 */
export function isWallVisibleOnMap(wall, mapId) {
  return !!wall
    && wall.active === true
    && !!wall.currentVersionId
    && !wall.deleting
    && !!mapId
    && wall.mapId === mapId
    && Number.isFinite(wall.x)
    && Number.isFinite(wall.y);
}

/** List form of {@link isWallVisibleOnMap}. */
export function visibleMapWalls(walls, mapId) {
  return (walls || []).filter((w) => isWallVisibleOnMap(w, mapId));
}

/** `#spraywall`, `#spraywall/{wallId}` or `#spraywall/{wallId}/{sub}`. */
export function sprayWallHash(wallId, sub) {
  return "#" + SPRAY_WALL_TAB + (wallId ? `/${wallId}` : "") + (wallId && sub ? `/${sub}` : "");
}

/**
 * Open the Spraywall tab at a wall. `setTab` pushes one history entry (`#spraywall`)
 * so Back returns to where the user was; the wall id is then written with
 * `replaceState` (no second entry) and a synthetic `hashchange` tells the tab —
 * `replaceState` itself is silent.
 */
export function navigateToSprayWall(setTab, wallId, sub) {
  setTab(SPRAY_WALL_TAB);
  window.history.replaceState(null, "", window.location.pathname + window.location.search + sprayWallHash(wallId, sub));
  try { window.dispatchEvent(new HashChangeEvent("hashchange")); } catch {}
}

/**
 * Where a wall currently sits. A wall counts as placed only if its `mapId` still
 * names an existing map — a deleted map leaves the placement fields behind, and
 * the editor must then offer "place" rather than "move".
 */
export function placementStatus(wall, maps) {
  if (wall && typeof wall.mapId === "string" && Number.isFinite(wall.x) && Number.isFinite(wall.y)) {
    const map = (maps || []).find((m) => m.id === wall.mapId);
    if (map) return { placed: true, mapId: wall.mapId, mapName: map.name || "" };
  }
  return { placed: false };
}

/**
 * Enter the map's placement mode for a wall. A placed wall opens on its own map
 * so it can be moved; an unplaced one stays on the map the user was last on.
 */
export function startSprayWallPlacement({ wall, maps, setActiveMapId, setTab, setPending }) {
  if (!wall?.id) return;
  const status = placementStatus(wall, maps);
  if (status.placed) setActiveMapId(wall.mapId);
  setTab("map");
  setPending({ kind: "sprayWall", wallId: wall.id, name: wall.name || "" });
}

// The two patches use only keys the client may write on a wall document
// (sprayWallClientKeys() in firestore.rules).

/** Update payload for placing (or moving) a wall. */
export function sprayWallPlacementPatch(mapId, x, y, uid) {
  return buildSprayWallPlacement({ mapId, x, y, uid, timestamp: serverTimestamp() });
}

/** Update payload for taking a wall off the map; the wall itself is untouched. */
export function sprayWallRemovalPatch(uid) {
  return {
    mapId: deleteField(),
    x: deleteField(),
    y: deleteField(),
    updatedAt: serverTimestamp(),
    lastModifiedBy: uid,
  };
}
